import { useTranslation } from "react-i18next";
import { InteractiveMaterialResponseDto } from "@packages/api/madlenAPI.schemas";
import { PDFViewer } from "@core/pdf-viewer";
import { Text } from "@core/text";
import { Button } from "@core/button";
import { SendHorizonalIcon, Loader2 } from "lucide-react";
import {
  useEditInteractiveMaterialSpecs,
  getGetInteractiveMaterialQueryKey,
} from "@packages/api/madlenAPI";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { VideoPlayer } from "./utils";

interface WelcomeTabProps {
  material: InteractiveMaterialResponseDto;
  clone?: boolean;
  cloneLoading?: boolean;
  cloneHandler?: () => void;
}

const WelcomeTab = ({
  material,
  clone,
  cloneLoading,
  cloneHandler,
}: WelcomeTabProps) => {
  const { t } = useTranslation();
  const [isUpdating, setIsUpdating] = useState(false);
  const queryClient = useQueryClient();

  const { mutate: updateMaterialSpecs } = useEditInteractiveMaterialSpecs({
    mutation: {
      onSuccess: () => {
        queryClient
          .invalidateQueries({
            queryKey: getGetInteractiveMaterialQueryKey(material.id),
          })
          .then(() => {
            setIsUpdating(false);
          });
      },
      onError: () => {
        setIsUpdating(false);
      },
    },
  });

  const setCaptureOnlyFirstEvents = (captureOnlyFirstEvents: boolean) => {
    setIsUpdating(true);
    updateMaterialSpecs({
      id: material.id,
      data: {
        captureStudentsFirstAnswersOnly: captureOnlyFirstEvents,
      },
    });
  };

  return (
    <div className="h-[calc(100vh-20rem)] max-h-[800px] min-h-[600px] flex flex-col items-center justify-center space-y-4">
      <div
        className="w-full max-w-xl text-center -mt-10"
        data-testid="welcome-tab-title"
      >
        <Text variant={"h2"}>
          {material.generatorMode === "YOUTUBE"
            ? "interactive_material.enhanceVideo"
            : material.generatorMode === "PDF"
              ? "interactive_material.enhancePdf"
              : "interactive_material.enhanceVideo"}
        </Text>
        <Text
          variant={"p"}
          status="subtle"
          size="lg"
          className="max-w-xl mx-auto"
        >
          {material.generatorMode === "YOUTUBE"
            ? t("interactive_material.enhanceVideoDescription")
            : material.generatorMode === "PDF"
              ? t("interactive_material.enhancePdfDescription")
              : t("interactive_material.enhanceVideoDescription")}
        </Text>
      </div>

      <div className="w-full max-w-xl h-[80%]">
        <div
          className={`${
            material.generatorMode === "PDF" ? "h-full" : "aspect-video"
          } w-full bg-gray-100 dark:bg-gray-700 rounded-2xl overflow-hidden shadow-xl border border-gray-200/50 dark:border-gray-700/50`}
        >
          {material.generatorMode === "YOUTUBE" && material.youtubeUrl && (
            <VideoPlayer url={material.youtubeUrl} className="w-full h-full" />
          )}
          {material.generatorMode === "PDF" && material.pdfUrl && (
            <PDFViewer url={material.pdfUrl} />
          )}
        </div>
        {!clone && (
          <div className="sm:col-span-2 flex items-center space-x-2 p-3 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
            <div className="relative flex items-center">
              <input
                type="checkbox"
                id="captureOnlyFirstEvents"
                checked={material.captureStudentsFirstAnswersOnly}
                onChange={(e) => setCaptureOnlyFirstEvents(e.target.checked)}
                className={`h-4 w-4 rounded border-gray-300 text-rose-600 focus:ring-rose-500 dark:border-gray-600 dark:bg-gray-700 dark:focus:ring-rose-600 ${isUpdating ? "opacity-50 cursor-not-allowed" : ""}`}
                disabled={isUpdating}
              />
              {isUpdating && (
                <Loader2 className="animate-spin h-4 w-4 absolute text-rose-600 left-0" />
              )}
            </div>
            <label
              htmlFor="captureOnlyFirstEvents"
              className={`text-sm font-medium text-gray-700 dark:text-gray-300 select-none ${isUpdating ? "opacity-70" : ""}`}
            >
              {t("interactive_material.create.captureOnlyFirstEvents")}
            </label>
          </div>
        )}
        {clone && (
          <div className="mt-6 flex justify-center">
            <Button
              onClick={() => cloneHandler?.()}
              variant="solid"
              size="lg"
              rounded="lg"
              bgColor="bg-rose-500 hover:bg-rose-600"
              textColor="text-white"
              className="flex items-center gap-2"
              disabled={cloneLoading}
              loading={cloneLoading}
            >
              <SendHorizonalIcon size={20} />
              {t("interactive_material.clone.cloneToMyAccount")}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default WelcomeTab;
